import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, Printer } from 'lucide-react'

import { api } from '@/api/client'
import { useFileDownload } from '@/lib/useFileDownload'
import { useTabField } from '@/stores/tabState'
import { formatNum } from '@/lib/utils'

interface RegisterLine {
  partner_id: number
  partner_code: string
  partner_name: string
  qty: string
  unit: string
  order_no: string
  note?: string
}
interface RegisterGroup {
  item_id: number
  item_code: string
  item_name: string
  unit: string
  total_qty: string
  lines: RegisterLine[]
}
interface RegisterResp {
  date: string
  total_items: number
  total_lines: number
  groups: RegisterGroup[]
}

export function PurchaseRegisterTab() {
  const { downloading, download } = useFileDownload()
  // 조회 일자는 탭을 오가도 유지(구매장 스코프).
  const [date, setDate] = useTabField('purchase-register', 'date', '')   // 기본 빈 날짜(직접 지정)

  const { data, isLoading } = useQuery({
    queryKey: ['purchase-register', date],
    queryFn: () => api<RegisterResp>(`/api/delivery/purchase-register?date=${date}`),
    enabled: !!date,   // 날짜 지정 전엔 조회 안 함
  })
  const groups = data?.groups ?? []

  async function downloadExcel() {
    await download(`/api/delivery/purchase-register/export?date=${date}`, `구매장_${date}.xlsx`)
  }

  // 2열 side-by-side: 짝수 index 왼쪽, 홀수 오른쪽
  const pairs: Array<[RegisterGroup | undefined, RegisterGroup | undefined]> = []
  for (let i = 0; i < groups.length; i += 2) {
    pairs.push([groups[i], groups[i + 1]])
  }

  return (
    <div className="max-w-[1200px] mx-auto p-6">
      {/* 툴바 — 인쇄 시 숨김 */}
      <div className="mb-4 flex items-center gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">일자</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"/>
        </div>
        <span className="text-xs text-slate-500 tabular-nums">
          {data ? `${data.total_items}품목 · ${data.total_lines}건` : '—'}
        </span>
        <div className="flex-1"/>
        <button onClick={() => window.print()} disabled={!groups.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          <Printer className="w-4 h-4"/>
          인쇄
        </button>
        <button onClick={downloadExcel} disabled={downloading || !groups.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>

      {/* 인쇄용 문서 */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden print:border-0 print:rounded-none">
        {/* 헤더 */}
        <div className="text-center py-6 border-b border-slate-200 print:py-3">
          <h1 className="text-2xl font-bold tracking-[0.4em] text-slate-900 print:text-xl">구 매 장</h1>
          <div className="mt-2 text-sm text-slate-600 tabular-nums">
            {date}
            {data && <span className="ml-4 text-slate-400">· {data.total_items}품목</span>}
          </div>
        </div>

        {/* 본문: 2열 side-by-side */}
        <div className="p-4 print:p-2">
          {isLoading && (
            <div className="text-center py-12 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2"/>불러오는 중...
            </div>
          )}
          {!isLoading && !groups.length && (
            <div className="text-center py-16 text-slate-400 text-sm">
              해당 일자의 출고 데이터가 없습니다.
            </div>
          )}
          {!isLoading && groups.length > 0 && (
            <div className="grid grid-cols-2 gap-x-6">
              <ColumnPanel pairs={pairs} side="left"/>
              <ColumnPanel pairs={pairs} side="right"/>
            </div>
          )}
        </div>
      </div>

      {/* 인쇄 스타일 */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  )
}

function ColumnPanel({ pairs, side }: {
  pairs: Array<[RegisterGroup | undefined, RegisterGroup | undefined]>
  side: 'left' | 'right'
}) {
  return (
    <div className="text-xs">
      {pairs.map(([l, r], idx) => {
        const g = side === 'left' ? l : r
        if (!g) return <div key={idx}/>
        return <ItemGroup key={g.item_id} group={g}/>
      })}
    </div>
  )
}

function ItemGroup({ group }: { group: RegisterGroup }) {
  return (
    <div className="mb-3 break-inside-avoid">
      <div className="flex items-baseline justify-between border-b border-slate-300 pb-0.5 mb-1">
        <span className="font-bold text-slate-900 text-sm">{group.item_name}</span>
        <span className="text-slate-500 tabular-nums text-xs">
          합계 {formatNum(group.total_qty)} {group.unit}
        </span>
      </div>
      <table className="w-full">
        <colgroup>
          <col className="w-14"/>
          <col className="w-8"/>
          <col/>
        </colgroup>
        <tbody>
          {group.lines.map((ln, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="text-right tabular-nums pr-1 py-0.5 text-slate-900">{formatNum(ln.qty)}</td>
              <td className="text-left text-slate-500 pr-2">{ln.unit}</td>
              <td className="text-left text-slate-700 truncate">
                {ln.partner_name}
                {ln.note && <span className="text-rose-600"> ({ln.note})</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
